<template>
  <div>
    <b-overlay
      :show="resellersResponses('resellersList') && resellersEmpty"
      spinner-type="border"
      variant="light"
      spinner-variant="primary"
      opacity="1"
      rounded>
      <div class="d-flex align-items-end pt-2 pb-6">
        <div class="d-flex flex-column">
          <h1 class="h5 mb-2">
            Resellers
          </h1>
          <b-breadcrumb class="breadcrumb-transparent breadcrumb-line p-0 m-0">
            <b-breadcrumb-item :to="{ name: 'Dashboard' }">
              <i class="fa fa-home fa-sm mr-2"></i>
              Home
            </b-breadcrumb-item>
            <b-breadcrumb-item active>Resellers</b-breadcrumb-item>
          </b-breadcrumb>
        </div>
      </div>

      <b-card
        no-body
        class="card-custom card-border">
        <b-card-body>
          <b-row class="mb-8">
            <b-col
              cols="12"
              sm="5"
              md="3"
              lg="2">
              <b-form-select
                :disabled="activeTariffsEmpty"
                :value="resellersGetParams.tariff"
                @change="
                  setResellersGetParam($event, 'tariff')
                  setResellersGetParam(1, 'page')
                "
                class="mb-2">
                <template #first>
                  <b-form-select-option :value="null">
                    All Tariffs
                  </b-form-select-option>
                </template>
                <b-form-select-option
                  disabled
                  :value="null">
                  ─────────────────────
                </b-form-select-option>
                <b-form-select-option
                  v-for="tariff in activeTariffs"
                  :key="`tariff-${tariff._id}`"
                  :value="tariff._id">
                  {{ tariff.name }}
                </b-form-select-option>
              </b-form-select>
            </b-col>
            <b-col
              cols="12"
              sm="4"
              md="3"
              lg="2">
              <b-form-select
                :value="resellersGetParams.status"
                value-field="key"
                @change="
                  setResellersGetParam($event, 'status')
                  setResellersGetParam(1, 'page')
                "
                class="mb-2"
                style="max-width: 140px;">
                <template #first>
                  <b-form-select-option :value="null">
                    All Statuses
                  </b-form-select-option>
                </template>
                <b-form-select-option
                  disabled
                  :value="null">
                  ──────────
                </b-form-select-option>
                <b-form-select-option
                  v-for="status in resellersStatuses"
                  :key="`status-${status._id}`"
                  :value="status.key">
                  {{ status.name }}
                </b-form-select-option>
              </b-form-select>
            </b-col>
          </b-row>

          <b-table
            :items="resellersList"
            :fields="visibleFields"
            :class="{ 'table-hover' : !resellersEmpty }"
            stacked="md"
            head-variant=""
            show-empty
            responsive>
            
            <!-- custom headings -->
            <template #head(id)="data">
              <sort-button
                :data="data"
                :getParams="resellersGetParams"
                @change="key => handleSortDirection(key)" />
            </template>
            <template #head(created)="data">
              <sort-button
                :data="data"
                :getParams="resellersGetParams"
                @change="key => handleSortDirection(key)" />
            </template>

            <!-- custom cells -->
            <template #cell(id)="data">
              <span>{{ data.item.id }}</span>
            </template>
            <template #cell(created)="data">
              <span>{{ data.item.created | moment("DD.MM.YYYY, HH:mm") }}</span>
            </template>
            <template #cell(devices)="data">
              <reseller-capacity
                :item="data.item"
                :param="'devices'" />
            </template>
            <template #cell(users)="data">
              <reseller-capacity
                :item="data.item"
                :param="'users'" />
            </template>
            <template #cell(active)="data">
              <div class="d-flex justify-center-start justify-content-md-center">
                <!-- Wrapper for fake element + checkbox -->
                <div
                  class="position-relative"
                  style="width: fit-content;">
                  <!-- Fake element above checkbox that TRIGGERS event -->
                  <div
                    class="w-100 h-100 position-absolute"
                    style="z-index: 20;"
                    role="button"
                    @click="handleStatusChange(data.item)" />
                  <!-- Checkbox that REPRESENTS current status, not changes it -->
                  <b-form-checkbox
                    :checked="data.item.active"
                    size="lg"
                    switch
                    name="switch-vpn-server"
                    class="switch-success ml-0 ml-md-3 mb-1 mb-md-2" />
                </div>
              </div>
            </template>
            <template #cell(integrations)="data">
              <div v-if="resellerHasIntegrations(data.item._id)">
                <div class="table-grid-items">
                  <span
                    v-for="integration in resellerIntegrations(data.item._id)"
                    :key="`reseller-${data.item._id}-${integration.slug}`"
                    v-b-tooltip.hover
                    :title="integration.name"
                    class="svg-icon svg-icon-sm cursor-help">
                    <inline-svg
                      :src="`media/svg/icons/Integrations/${integration.slug}.svg`"
                      class="svg-icon m-0" />
                  </span>
                </div>
              </div>
              <i
                v-else
                class="fa fa-minus fa-xs opacity-50" />
            </template>
            <template #cell(edit)="data">
              <b-dropdown
                no-caret
                :boundary="resellersListLength < 5 ? 'window' : 'scrollParent'"
                right
                variant="outline-primary"
                toggle-class="btn-icon"
                size="sm"
                class="mr-4 dropdown-inline">
                <template v-slot:button-content>
                  <i class="fa fa-ellipsis-h fa-sm"></i>
                </template>
                <b-dropdown-item-button
                  :disabled="resellersResponses('getReseller')"
                  @click="showModal('changeTariffModal', data.item.id)">
                  <i class="fa fa-chart-bar fa-sm mr-1"></i>
                  Change tariff
                </b-dropdown-item-button>
                <b-dropdown-item-button
                  :disabled="!data.item.active || resellersResponses('getReseller')"
                  @click="showModal('generateUserModal', data.item.id)">
                  <i class="fa fa-user fa-sm mr-1"></i>
                  Generate user
                </b-dropdown-item-button>
                <b-dropdown-item-button
                  :disabled="resellersResponses('getReseller')"
                  @click="showModal('addonModal', data.item.id)">
                  <i class="fa fa-plus fa-sm mr-1"></i>
                  Add devices / users
                </b-dropdown-item-button>
                <b-dropdown-item-button
                  :disabled="resellersResponses('getReseller') || resellersResponses('getResellerServers')"
                  @click="showModal('resellerServers', data.item.id)">
                  <i class="fa fa-server fa-sm mr-1"></i>
                  Show servers list
                </b-dropdown-item-button>
                <b-dropdown-item-button
                  @click="$router.push({
                    name: 'ResellerIntegrations',
                    params: { id: data.item.id }
                  }).catch(() => {})">
                  <i class="fa fa-sitemap fa-sm mr-1"></i>
                  Manage integrations
                </b-dropdown-item-button>
                <b-dropdown-item-button disabled>
                  <i class="fa fa-link fa-sm mr-1"></i>
                  Manage webhooks
                </b-dropdown-item-button>
              </b-dropdown>
              <b-button
                :id="`deleteButton-${data.item._id}`"
                v-wave
                size="sm"
                variant="outline-primary"
                class="btn-icon"
                @click="
                  selectReseller(data.item),
                  showDeletePopover(data.item._id)
                ">
                <i class="fa fa-trash fa-sm"></i>
              </b-button>
            </template>

            <!-- empty state -->
            <template #empty="scope">
              <no-data-found />
            </template>
          </b-table>

          <div class="w-100 d-flex align-items-center flex-wrap pt-6">
            <b-dropdown
              variant="hover-light-primary"
              id="custiomize-table"
              no-caret
              size="sm"
              class="mr-auto">
              <template #button-content>
                <span>Customize Table</span>
                <i class="fa fa-cog fa-sm pl-2 pr-0"></i>
              </template>
              <div class="d-flex flex-column px-3">
                <b-form-checkbox
                  v-for="field in fields"
                  :key="`dropdown-item-${field.key}`"
                  v-model="field.visible"
                  :disabled="field.default"
                  size="md"
                  class="my-1">
                  {{ field.label }}
                </b-form-checkbox>
              </div>
            </b-dropdown>
            <div class="d-flex align-items-center">
              <div class="d-flex align-items-center mr-2">
                <span class="font-weight-bold font-size-sm">Per Page:</span>
                <b-form-select
                  size="sm"
                  :value="resellersLimit"
                  :options="limits"
                  class="w-auto ml-2"
                  @change="
                    setResellersGetParam(1, 'page'),
                    setResellersGetParam($event, 'limit')
                  " />
              </div>
              <!--
                Don't remove v-if statement!
                https://github.com/bootstrap-vue/bootstrap-vue/issues/3372#issuecomment-596158197
              -->
              <b-pagination
                v-if="!resellersEmpty"
                :value="resellersPage"
                :total-rows="totalResellers"
                :per-page="resellersLimit"
                size="md"
                first-number
                last-number
                class="mb-0 ml-auto"
                @change="setResellersGetParam($event, 'page')">
                <template #prev-text>
                  <i class="fa fa-xs fa-chevron-left"></i>
                </template>
                <template #next-text>
                  <i class="fa fa-xs fa-chevron-right"></i>
                </template>
              </b-pagination>
            </div>
          </div>
        </b-card-body>
      </b-card>
    </b-overlay>

    <b-popover
      v-for="reseller in resellersList"
      :key="`popover-${reseller._id}`"
      :target="`deleteButton-${reseller._id}`"
      :ref="`popover-${reseller._id}`"
      triggers="manual blur"
      placement="left">
      <p>
        Are you sure you want to delete this reseller? This action will permanently delete all users (<span class="font-weight-bold">{{ selectedReseller.users.total }}</span>) and their access.
      </p>
      <div class="d-flex w-100">
        <b-button
          @click="hideDeletePopover(reseller._id)"
          variant="outline-primary"
          size="sm">
          Cancel
        </b-button>
        <b-button
          variant="danger"
          size="sm"
          class="ml-4 text-center justify-content-center flex-grow-1"
          @click="confirmDelete(), hideDeletePopover(reseller._id)">
          <i class="fa fa-check fa-sm"></i>
          <span>Yes, delete</span>
        </b-button>
      </div>
    </b-popover>

    <modal-shutdown-reseller />
    <modal-change-tariff />
    <modal-generate-user />
    <modal-reseller-servers />
    <modal-addon />
    
  </div>
</template>

<script>
import {
  // actions
  GET_RESELLERS,
  GET_RESELLER,
  SWITCH_RESELLER,
  DELETE_RESELLER,
  GET_RESELLER_SERVERS,

  // mutations
  SELECT_RESELLER,
  UNSELECT_RESELLER,
  CLEAR_RESELLERS,
  SET_RESELLERS_GET_PARAMS,
  SET_RESELLERS_GET_PARAM,
  CLEAR_RESELLERS_GET_PARAMS,

  // constans
  DEFAULT_RESELLERS_GET_PARAMS,
} from '@/core/services/store/resellers.module'
import { GET_TARIFFS } from '@/core/services/store/tariffs.module'
import { GET_COUNTRIES } from '@/core/services/store/countries.module'
import { mapGetters } from 'vuex'
import { eventBus } from '@/main'
import {
  showToast,
  showDeletePopover,
  hideDeletePopover,
  restoreTableColumns
} from '@/core/utils'
import { regex_digits } from '@/core/regex'
import ModalShutdownReseller from './components/ModalShutdownReseller.vue'
import ModalChangeTariff from './components/ModalChangeTariff.vue'
import ModalGenerateUser from './components/ModalGenerateUser.vue'
import ModalResellerServers from './components/ModalResellerServers.vue'
import ModalAddon from './components/ModalAddon.vue'
import ResellerCapacity from './components/ResellerCapacity.vue'
import SortButton from '@/view/layout/controls/SortButton.vue'

export default {
  name: 'ResellersList',
  components: {
    ModalShutdownReseller,
    ModalChangeTariff,
    ModalGenerateUser,
    ModalResellerServers,
    ModalAddon,
    ResellerCapacity,
    SortButton,
  },
  data() {
    return {
      fields: [
        {
          label: 'ID',
          key: 'id',
          class: ['align-middle', 'font-size-sm', 'font-weight-bold', 'text-nowrap', 'pl-5'],
          visible: true,
          default: true,
          thStyle: 'width: 100px;'
        },
        {
          label: 'Created',
          key: 'created',
          class: ['align-middle', 'font-size-sm', 'text-nowrap'],
          visible: true,
          default: true,
        },
        {
          label: 'Tariff',
          key: 'tariff.name',
          class: ['align-middle', 'text-nowrap'],
          visible: true,
          default: true,
        },
        {
          label: 'Status',
          key: 'active',
          class: ['align-middle', 'text-nowrap', 'text-center'],
          visible: true,
        },
        {
          label: 'Devices',
          key: 'devices',
          class: ['align-middle', 'text-nowrap', 'text-left text-md-right'],
          visible: true,
        },
        {
          label: 'Users',
          key: 'users',
          class: ['align-middle', 'text-nowrap', 'text-left text-md-right'],
          visible: true,
        },
        {
          label: 'Integrations',
          key: 'integrations',
          class: ['align-middle', 'text-nowrap', 'pl-0 pl-md-20'],
          visible: true,
        },
        {
          label: 'Actions',
          key: 'edit',
          class: ['align-middle', 'text-md-right', 'text-nowrap', 'pr-5'],
          visible: true,
          default: true,
        }
      ],
      limits: [10, 25, 50, 100],
      resellersStatuses: [
        { _id: 'active', name: 'Active', key: 'on' },
        { _id: 'disabled', name: 'Disabled', key: 'off' }
      ],

      showToast,
      showDeletePopover,
      hideDeletePopover,
      restoreTableColumns,
    }
  },
  computed: {
    ...mapGetters([
      'resellersList',
      'resellersListLength',
      'resellersEmpty',
      'resellersGetParams',
      'resellersPage',
      'resellersLimit',
      'totalResellers',
      'totalResellerPages',
      'selectedReseller',
      'selectedResellerId',
      'resellerCapacity',
      'resellerHasIntegrations',
      'resellerIntegrations',
      'resellersResponses',

      'activeTariffs',
      'activeTariffsLength',
      'activeTariffsEmpty',
      'archievedTariffs',
      'archievedTariffsLength',
      'allTariffs',
      'allTariffsLength',
      'allTariffsEmpty',
      'tariffVpnServers',
      'tariffVpnServersEmpty',
      'tariffIsArchieved',
      'tariffGroupEmpty',
      'markedForSwitchTariff',
      'tariffsResponses',
    ]),

    visibleFields() {
      return this.fields.filter(field => field.visible)
    }
  },
  watch: {
    resellersGetParams: {
      handler: function(newVal) {
        // sanitize null values
        const cleanedQuery = { ...newVal }
        Object.keys(cleanedQuery).forEach(key => {
          if (!cleanedQuery[key] || cleanedQuery[key] == 'null') {
            delete cleanedQuery[key]
          }
        })
        this.$router.push({
          name: this.$options.name,
          query: cleanedQuery
        }).catch(() => {})
      },
      deep: true
    },

    $route() {
      this.handleUrl()
    },

    visibleFields() {
      localStorage.setItem('resellerTableColumns', JSON.stringify(this.fields))
    },

    resellersEmpty(newVal) {
      // this function forces to the last available page
      // if user manually choosed page that doesn't exist
      if (
        newVal &&
        this.resellersPage > this.totalResellerPages &&
        this.totalResellerPages > 1) {
        this.setResellersGetParam(this.totalResellerPages, 'page')
        this.$router.push({
          name: this.$options.name,
          query: { ...this.$route.query, page: this.totalResellerPages }
        }).catch(() => {})
        this.getResellers()
      }
    },
  },
  created() {
    this.$store.dispatch(GET_COUNTRIES)
    this.clearResellers()
    this.getTariffs('active')
    this.restoreTableColumns('resellerTableColumns')
    this.handleUrl()

    eventBus.$on('showToast/ResellersList', ({ message, variant }) => {
      this.showToast(message, variant)
    })
  },
  destroyed() {
    eventBus.$off('showToast/ResellersList')

    this.$store.commit(CLEAR_RESELLERS_GET_PARAMS)
  },
  methods: {
    validateQueryParams(queryParams) {
      const allowedKeys = Object.keys(DEFAULT_RESELLERS_GET_PARAMS)

      // Check if there are no extra query parameters
      const extraKeys = Object.keys(queryParams).filter(
        key => !allowedKeys.includes(key)
      )

      // Validate each query parameter
      const validLimit = this.limits.includes(Number(queryParams.limit))
      const validPage = regex_digits.test(queryParams.page)
      const validSort = ['id', 'created'].includes(queryParams.sort)
      const validOrd = ['asc', 'desc'].includes(queryParams.ord)
      let validStatus = true

      if (queryParams.status) {
        validStatus = ['on', 'off'].includes(queryParams.status)
      }

      return (
        extraKeys.length === 0 &&
        validLimit &&
        validPage &&
        validSort &&
        validOrd &&
        validStatus
      )
    },

    handleUrl() {
      const queryParams = this.$route.query
      const valid = this.validateQueryParams(queryParams)

      if (valid) {
        this.applyUrlQueries()

        Object.keys(this.resellersGetParams).forEach(key => {
          this.resellersGetParams[key] = queryParams[key] ? queryParams[key] : null
        })
      } else {
        this.clearUrlQueries()
      }

      this.getResellers()
    },

    applyUrlQueries() {
      this.$store.commit(SET_RESELLERS_GET_PARAMS, this.$route.query)
    },

    clearUrlQueries() {
      this.$store.commit(CLEAR_RESELLERS_GET_PARAMS)
      this.$router.push({
        name: this.$options.name,
        query: {}
      }).catch(() => {})
    },

    getResellers() {
      this.$store
        .dispatch(GET_RESELLERS)
        .catch(err => {
          const error = err.response.data
          if (!error.errors || !error.errors[0]) {
            return
          }
          const error_code = error.errors[0].code
          if (error_code === 'tariff_invalid') {
            this.clearUrlQueries()
          }
        })
    },

    clearResellers() {
      this.$store.commit(CLEAR_RESELLERS)
    },

    setResellersGetParam(value, param) {
      this.$store.commit(SET_RESELLERS_GET_PARAM, { value, param })
    },

    handleSortDirection(key) {
      if (key === this.resellersGetParams.sort) {
        this.setResellersGetParam(
          this.resellersGetParams.ord === 'desc' ? 'asc' : 'desc',
          'ord'
        )
      } else {
        this.setResellersGetParam('asc', 'ord')
      }

      this.setResellersGetParam(key, 'sort')
    },

    selectReseller(reseller) {
      this.$store.commit(SELECT_RESELLER, reseller)
    },

    unselectReseller() {
      this.$store.commit(UNSELECT_RESELLER)
    },

    dataIsReady(data) {
      let result = []
      for (let i = 0; i < data.length; i++) {
        result.push(Array.isArray(data[i]) || typeof data == 'object')
      }
      return result.every(el => el)
    },

    showModal(type, reseller_id) {
      let getReseller = () => {
        if (!reseller_id) {
          return true
        }
        return this.$store
          .dispatch(GET_RESELLER, { id: reseller_id })
          .then(data => {
            this.selectReseller(data)
          })
      }

      let requirements = {
        changeTariffModal: [ getReseller ],
        generateUserModal: [ getReseller ],
        addonModal: [ getReseller ],
        resellerServers: [
          getReseller,
          () => this.$store.dispatch(GET_RESELLER_SERVERS, { reseller_id })
        ],
      }

      if (requirements.hasOwnProperty(type)) {
        Promise
          .all(requirements[type].map(promiseFn => promiseFn()))
          .then(res => {
            if (this.dataIsReady(res)) {
              eventBus.$emit('openModal', type)
            }
          })
      } else {
        eventBus.$emit('openModal', type)
      }
    },

    confirmDelete() {
      this.$store.dispatch(DELETE_RESELLER)
    },

    handleStatusChange(reseller) {
      this.selectReseller(reseller)

      if (reseller.active) {
        eventBus.$emit('openModal', 'shutdownResellerConfirm')
      } else {
        this.$store
          .dispatch(SWITCH_RESELLER, { id: reseller.id })
          .then(() => {
            if (this.$route.query.status) {
              this.getResellers()
            }
          })
      }
    },

    getTariffs(type) {
      this.$store.dispatch(GET_TARIFFS, { type })
    },
  }
}
</script>
